import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import mixpanel from 'mixpanel-browser';

export function Navigation() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isCategoryActive = (category: string) => {
    const searchParams = new URLSearchParams(location.search);
    return location.pathname === '/blog' && searchParams.get('category') === category;
  };

  const handleMenuClick = (page: string) => {
    if (page === 'my-brain') {
      mixpanel.track('open_chat', { source: 'menu' });
    }
    // Track Website visits for each menu page
    const pageMap: Record<string, string> = {
      'ai': 'ai',
      'startups': 'startups',
      'traveling': 'traveling',
      'other': 'other-learning',
      'home': 'home-page',
    };
    if (pageMap[page]) {
      mixpanel.track('Website visits', { page: pageMap[page] });
    }
  };

  const menuItems = [
    { to: '/projects', label: 'Projects', isActive: isActive('/projects') },
    { to: '/blog?category=ai', label: 'AI', isActive: isCategoryActive('ai') },
    { to: '/blog?category=startups', label: 'Startups', isActive: isCategoryActive('startups') },
    { to: '/traveling', label: 'Traveling', isActive: isActive('/traveling') },
    { to: '/blog?category=other', label: 'Other Learnings', isActive: isCategoryActive('other') },
    { 
      to: '/my-brain', 
      label: (
        <span className="flex items-center gap-2">
          Second Brain
          <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded-full font-medium">BETA</span>
        </span>
      ), 
      isActive: isActive('/my-brain') 
    },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 h-16 flex items-center">
        {/* Logo */}
        <div className="w-[200px]">
          <Link to="/" className="text-xl font-bold hover:no-underline">
            🔮 Product Oracle
          </Link>
        </div>
        
        {/* Desktop Menu */}
        <div className="hidden md:flex items-center justify-center flex-1 space-x-4">
          {menuItems.map((item) => {
            let pageKey = '';
            if (item.to === '/') pageKey = 'home';
            else if (item.to === '/blog?category=ai') pageKey = 'ai';
            else if (item.to === '/blog?category=startups') pageKey = 'startups';
            else if (item.to === '/traveling') pageKey = 'traveling';
            else if (item.to === '/blog?category=other') pageKey = 'other';
            else if (item.to === '/my-brain') pageKey = 'my-brain';
            return (
              <Link key={item.to} to={item.to} onClick={() => handleMenuClick(pageKey)}>
                <Button 
                  variant={item.isActive ? 'default' : 'ghost'}
                  size="sm"
                >
                  {item.label}
                </Button>
              </Link>
            );
          })}
        </div>

        {/* Theme Toggle - Desktop */}
        <div className="hidden md:flex items-center justify-end w-[200px]">
          <ThemeToggle />
        </div>

        {/* Mobile Menu Button */}
        <div className="flex md:hidden items-center gap-2 ml-auto">
          <ThemeToggle />
          <button
            className="p-2 hover:bg-accent rounded-md"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className="md:hidden border-t bg-background/95 backdrop-blur">
          <div className="container mx-auto px-4 py-4 flex flex-col space-y-2">
            {menuItems.map((item) => {
              let pageKey = '';
              if (item.to === '/') pageKey = 'home';
              else if (item.to === '/blog?category=ai') pageKey = 'ai';
              else if (item.to === '/blog?category=startups') pageKey = 'startups';
              else if (item.to === '/traveling') pageKey = 'traveling';
              else if (item.to === '/blog?category=other') pageKey = 'other';
              else if (item.to === '/my-brain') pageKey = 'my-brain';
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => { setIsMenuOpen(false); handleMenuClick(pageKey); }}
                  className="w-full"
                >
                  <Button 
                    variant={item.isActive ? 'default' : 'ghost'}
                    size="sm"
                    className="w-full justify-start"
                  >
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}